/** Thin client for the Orbis API. Same origin as the panel, so the session cookie just works. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function send<T>(method: string, path: string, init: { body?: BodyInit; headers?: HeadersInit } = {}) {
  const res = await fetch(`/api${path}`, { method, credentials: "same-origin", ...init });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const error = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)
      ?.error;
    throw new ApiError(
      res.status,
      error?.code ?? "internal",
      error?.message ?? "Ocurrió un error inesperado.",
      error?.details,
    );
  }
  return data as T;
}

function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return send<T>(
    method,
    path,
    body === undefined ? {} : { body: JSON.stringify(body), headers: { "content-type": "application/json" } },
  );
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
  /** Sends a multipart form (the browser sets its content type). */
  form: <T>(path: string, form: FormData) => send<T>("POST", path, { body: form }),
  /** Sends a file as the raw body; its name goes URL-encoded in x-file-name. */
  upload: <T>(path: string, file: Blob, name: string, type: string) =>
    send<T>("PUT", path, { body: file, headers: { "content-type": type, "x-file-name": encodeURIComponent(name) } }),
};

export function query(params: Record<string, string | number | undefined | null>) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : "";
}
